import React, {useState} from "react";
import {Table} from "react-bootstrap";
import {Link} from "@inertiajs/react";
import {PiCaretDown, PiCaretRight} from "react-icons/pi";
import useAnalysisData from "./useAnalysisData.js";
import AnalysisCard from "./AnalysisCard.jsx";
import YearValueCells from "./YearValueCells.jsx";
import YearHeaderCells from "./YearHeaderCells.jsx";
import SignedPercentValue from "./SignedPercentValue.jsx";
import CustomerNumberCell from "./CustomerNumberCell.jsx";
import HelpTooltip from "../HelpTooltip.jsx";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import {formatEuroWhole, hasAbility, referenceLabels, shortPriceGroup, shortSector, yearRangeExcludingCurrent} from "./format.js";
import {StickyTableArea} from "../StickyFilterBlock.jsx";

// Feste Breiten der ersten drei (beim horizontalen Scrollen fixierten)
// Spalten - nötig, damit die "left"-Position der zweiten und dritten Spalte
// feststeht (Sichtprüfung 14.09.2026, Befund 2). Kunde bekommt die
// geforderte Mindestbreite von 14rem (= 224px bei 16px Grundschrift).
const RANG_BREITE = 48;
const KUNDE_BREITE = 224;
const KDNR_BREITE = 110;
const KUNDE_LINKS = RANG_BREITE;
const KDNR_LINKS = RANG_BREITE + KUNDE_BREITE;

/**
 * Kundenrangliste eines Vertreters (bzw. des Pools "Nicht zugeordnet -
 * prüfen"): Rang, Kundennummer prominent mit Altnummer, Name, Branche,
 * Preisgruppe, Land, Jahresspalten, Hochrechnung, Plan (mit Kennzeichnung
 * des Ersatzwerts), die drei Abweichungsspalten und Trend. Jede Zeile lässt
 * sich zu den Artikelart-/Produktlinienzeilen des Kunden aufklappen - der
 * Pfeil links neben dem Namen und der Name selbst klappen gleichermaßen auf
 * und zu (ganze Namenszelle als Schaltfläche, kein Link mehr); die
 * Kundenseite bleibt darüber in der aufgeklappten Zeile über einen
 * dezenten Link "Zur Kundenseite" erreichbar, sofern das Recht
 * `abilities.kunde` vorliegt.
 *
 * Die Tabelle ist breiter als der Bildschirm (viele Jahres- und
 * Abweichungsspalten): Rang, Kunde und Kdnr. bleiben deshalb beim
 * horizontalen Scrollen sichtbar (sticky), die Kopfzeile bleibt beim
 * vertikalen Scrollen fixiert (Sichtprüfung 14.09.2026, Befund 2).
 */
export default function VertreterCustomerTable({filters, params, isPool}) {
    const {meta} = filters;
    const {data, loading, error} = useAnalysisData("/vertrieb/daten/vertreter", params, {skip: !params.rep});
    const [expanded, setExpanded] = useState({});

    const pastYears = yearRangeExcludingCurrent(filters.yearFrom, filters.yearTo, meta?.current_year);
    const labels = referenceLabels(data?.reference, meta);
    const hideCurrent = labels.current === null;
    const hideHr = labels.hr === null;
    const canLinkCustomer = hasAbility(meta, "kunde");
    const customers = data?.customers || [];

    const toggle = (key) => setExpanded((current) => ({...current, [key]: !current[key]}));

    return (
        <AnalysisCard
            title={isPool ? "Nicht zugeordnet – prüfen" : "Kundenrangliste"}
            help="Rangfolge nach Umsatz des laufenden Jahres. Plan ohne hinterlegtes Ziel zeigt das Maximum der vier Vorjahre."
            loading={loading}
            error={error}
            empty={!loading && customers.length === 0}
        >
            <StickyTableArea>
                <Table size="sm" hover className="mb-0 align-middle">
                    <thead className="sticky-top bg-white">
                        <tr>
                            <th className="va-sticky-col" style={{left: 0, width: RANG_BREITE, minWidth: RANG_BREITE}}>
                                <HeaderWithHelp help="Position nach Umsatz des Bezugsjahres (absteigend) innerhalb der angezeigten Kundenliste.">
                                    Rang
                                </HeaderWithHelp>
                            </th>
                            <th className="va-sticky-col" style={{left: KUNDE_LINKS, minWidth: KUNDE_BREITE, width: KUNDE_BREITE}}>Kunde</th>
                            <th className="va-sticky-col va-sticky-col-end" style={{left: KDNR_LINKS, minWidth: KDNR_BREITE, width: KDNR_BREITE}}>
                                <HeaderWithHelp help="weclapp-Kundennummer; kleinere Zahl darunter ist die frühere Majesty-Nummer, falls vorhanden.">
                                    Kdnr.
                                </HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Aus dem weclapp-Kundenstamm, aktueller Stand, gilt rückwirkend für alle Jahre. Standard wie in der Excel-Auswertung: Graffiti und Fine Arts.">
                                    Branche
                                </HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Preisgruppe des Kunden in weclapp.">PG</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Land laut weclapp-Kundenstamm.">Land</HeaderWithHelp>
                            </th>
                            {isPool && (
                                <th>
                                    <HeaderWithHelp help="Letzte bekannte Zuordnung aus der Vorlage, bevor der Kunde in den Pool fiel.">
                                        frühere Zuordnung
                                    </HeaderWithHelp>
                                </th>
                            )}
                            <YearHeaderCells pastYears={pastYears} currentLabel={labels.current} hrLabel={labels.hr} />
                            <th className="text-end va-num-th">
                                <HeaderWithHelp help='Planziel des Kunden; fehlt es, gilt das Maximum der vier Vorjahre (gekennzeichnet).'>
                                    {labels.plan}
                                </HeaderWithHelp>
                            </th>
                            <th className="text-end va-num-th">
                                <HeaderWithHelp help="Vorjahr gegenüber dem Jahr davor.">{labels.prevVsPrevprev}</HeaderWithHelp>
                            </th>
                            <th className="text-end va-num-th">
                                <HeaderWithHelp help="Hochrechnung (bzw. Bezugsjahr) gegenüber dem Vorjahr.">{labels.hrVsPrev}</HeaderWithHelp>
                            </th>
                            <th className="text-end va-num-th">
                                <HeaderWithHelp help="Hochrechnung (bzw. Bezugsjahr) gegenüber dem Planziel.">{labels.hrVsPlan}</HeaderWithHelp>
                            </th>
                            <th className="text-end va-num-th">
                                <HeaderWithHelp help="Hochrechnung (bzw. Bezugsjahr) gegen den Durchschnitt der vier Jahre davor." placement="left">
                                    {labels.trend}
                                </HeaderWithHelp>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {customers.map((customer) => {
                            const key = customer.customer_number;
                            const columnCount = 6 + (isPool ? 1 : 0) + pastYears.length + (hideCurrent ? 0 : 1) + (hideHr ? 0 : 1) + 5;
                            const kurzePreisgruppe = shortPriceGroup(customer.price_group);
                            const isExpanded = Boolean(expanded[key]);
                            return (
                                <React.Fragment key={key}>
                                    <tr>
                                        <td className="va-sticky-col" style={{left: 0, width: RANG_BREITE, minWidth: RANG_BREITE}}>{customer.rank}</td>
                                        <td className="va-sticky-col" style={{left: KUNDE_LINKS, minWidth: KUNDE_BREITE, width: KUNDE_BREITE}}>
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 d-flex align-items-center gap-1 text-body text-decoration-none w-100 text-start"
                                                style={{cursor: "pointer"}}
                                                aria-expanded={isExpanded}
                                                aria-label={`${customer.name} ${isExpanded ? "zuklappen" : "aufklappen"}`}
                                                onClick={() => toggle(key)}
                                            >
                                                <span className="flex-shrink-0 d-inline-flex" style={{width: 16}} aria-hidden="true">
                                                    {isExpanded ? <PiCaretDown /> : <PiCaretRight />}
                                                </span>
                                                <span className="va-customer-name flex-grow-1" style={{minWidth: 0}}>{customer.name}</span>
                                            </button>
                                        </td>
                                        <td className="va-sticky-col va-sticky-col-end" style={{left: KDNR_LINKS, minWidth: KDNR_BREITE, width: KDNR_BREITE}}>
                                            <CustomerNumberCell customerNumber={customer.customer_number} legacyNumbers={customer.legacy_numbers} />
                                        </td>
                                        <td>{shortSector(customer.sector) || "–"}</td>
                                        <td>
                                            {kurzePreisgruppe || "–"}
                                            {kurzePreisgruppe && kurzePreisgruppe !== customer.price_group && (
                                                <HelpTooltip placement="top" size={0.7} className="ms-1">
                                                    {customer.price_group}
                                                </HelpTooltip>
                                            )}
                                        </td>
                                        <td>{customer.country || "–"}</td>
                                        {isPool && <td>{customer.previous_rep || "–"}</td>}
                                        <YearValueCells years={customer.years} current={customer.current} hr={customer.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                        <td className="text-end text-nowrap">
                                            {formatEuroWhole(customer.plan)}
                                            {customer.plan_is_fallback && (
                                                <HelpTooltip placement="left" size={0.7} className="ms-1">
                                                    Kein Plan hinterlegt – Maximum der vier Vorjahre
                                                </HelpTooltip>
                                            )}
                                        </td>
                                        <td className="text-end text-nowrap"><SignedPercentValue value={customer.prev_vs_prevprev} /></td>
                                        <td className="text-end text-nowrap"><SignedPercentValue value={customer.hr_vs_prev} /></td>
                                        <td className="text-end text-nowrap"><SignedPercentValue value={customer.hr_vs_plan} /></td>
                                        <td className="text-end text-nowrap"><SignedPercentValue value={customer.trend} /></td>
                                    </tr>
                                    {isExpanded && canLinkCustomer && (
                                        <tr className="small">
                                            <td className="va-sticky-col" style={{left: 0, width: RANG_BREITE, minWidth: RANG_BREITE}} />
                                            <td
                                                colSpan={2}
                                                className="va-sticky-col va-sticky-col-end"
                                                style={{left: KUNDE_LINKS, paddingLeft: 28}}
                                            >
                                                <Link href={`/vertrieb/kunde/${customer.customer_number}`} className="text-muted">
                                                    Zur Kundenseite
                                                </Link>
                                            </td>
                                            <td colSpan={3 + (isPool ? 1 : 0)} />
                                            <td colSpan={pastYears.length + (hideCurrent ? 0 : 1) + (hideHr ? 0 : 1) + 5} />
                                        </tr>
                                    )}
                                    {isExpanded && (customer.lines || []).map((line) => (
                                        <tr key={`${key}-${line.article_type}-${line.product_line}`} className="text-muted small">
                                            <td className="va-sticky-col" style={{left: 0, width: RANG_BREITE, minWidth: RANG_BREITE}} />
                                            <td
                                                colSpan={2}
                                                className="va-sticky-col va-sticky-col-end"
                                                style={{left: KUNDE_LINKS, paddingLeft: 28}}
                                            >
                                                {line.article_type} – {line.product_line || "Nicht zugeordnet"}
                                            </td>
                                            <td colSpan={3 + (isPool ? 1 : 0)} />
                                            <YearValueCells years={line.years} current={line.current} hr={line.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                            <td colSpan={5} />
                                        </tr>
                                    ))}
                                    {isExpanded && (customer.lines || []).length === 0 && (
                                        <tr className="text-muted small">
                                            <td colSpan={columnCount} style={{paddingLeft: 28}}>Keine Positionen im gewählten Zeitraum.</td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {data?.total && (
                            <tr className="table-secondary fw-bold">
                                <td colSpan={3} className="va-sticky-col va-sticky-col-end table-secondary" style={{left: 0}}>Gesamtergebnis</td>
                                <td colSpan={3 + (isPool ? 1 : 0)} />
                                <YearValueCells years={data.total.years} current={data.total.current} hr={data.total.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                                <td colSpan={5} />
                            </tr>
                        )}
                    </tbody>
                </Table>
            </StickyTableArea>
        </AnalysisCard>
    );
}
